import connectionService from '@services/internal/connection.service';
import moment from 'moment';
import { call, put, select, takeLatest } from 'typed-redux-saga/macro';
import { getContactMessages, getMessagesFromLastMonth } from '../../helpers/strophe-helpers';
import {
  getUnreads,
  incrementUnreadForContact,
  isDuplicate,
  saveUnreads,
  sortLastMessages,
} from '../../helpers/unreads-helper';
import { isIE11, removeDuplicates } from '../../helpers/utils-helpers';
import DB, { DirectMessageModel } from '../../lib/db';
import { TDirectMessage, TLastMessage } from '../../types/direct-chat';
import TStore from '../../types/store';
import { directChatActionTypes } from '../ducks/direct-chat';

/**
 * Get messages from user specified by his JID
 *
 * @param param0  payload containing JabberId of a user
 * @author ukraine.tiger
 *
 */
function* getMessagesRequest({ payload }: any) {
  const contactJid = payload;
  yield put({
    type: directChatActionTypes.SET_LOADING,
    payload: true,
  });
  try {
    const connection = yield* call(connectionService.waitForConnection);
    const jabberId = yield* select((store: TStore) => store.users.currentUser?.jabberId);
    const currentUserId = yield* select((store: TStore) => store.users.currentUser?.id);

    let messagesFromDb: TDirectMessage[] = [];

    if (!isIE11) {
      messagesFromDb = yield DirectMessageModel.getMessagesByThread(contactJid);
    }

    let config: any = {
      max: 100,
      before: '',
    };

    if (messagesFromDb.length) {
      config = {
        after: messagesFromDb[messagesFromDb.length - 1].archivedId,
      };
    }

    const messages: TDirectMessage[] = yield* call(
      getContactMessages,
      connection,
      jabberId!,
      contactJid,
      config,
    );

    const withoutDuplicates = removeDuplicates(messages);
    const withCurrentUserId = withoutDuplicates.map((message) => ({
      ...message,
      currentUserId,
    }));

    const messagesFromXMPP: any = {};
    for (let idx = 0; idx < withCurrentUserId.length; idx = idx + 1) {
      messagesFromXMPP[withCurrentUserId[idx].uuid] = withCurrentUserId[idx];
    }

    const messagesFromIndexDb: any = {};
    for (let idx = 0; idx < messagesFromDb.length; idx = idx + 1) {
      messagesFromIndexDb[messagesFromDb[idx].uuid] = messagesFromDb[idx];
    }

    const finalMessagesObjects: any = { ...messagesFromIndexDb, ...messagesFromXMPP };
    const finalMessagesArray: any = [];

    const keys = Object.keys(finalMessagesObjects);
    for (let idx = 0; idx < keys.length; idx = idx + 1) {
      finalMessagesArray.push(finalMessagesObjects[keys[idx]]);
    }

    yield DirectMessageModel.bulkSave(finalMessagesArray);

    // reset unreads for current contact
    const unreads = getUnreads();
    unreads[contactJid] = 0;
    saveUnreads(unreads);
    yield put({ type: directChatActionTypes.GET_UNREADS_REQUEST });

    yield put({
      type: directChatActionTypes.GET_MESSAGES_SUCCESS,
      payload: finalMessagesArray,
    });
  } catch (err) {
    console.log(err);
  }
  yield put({
    type: directChatActionTypes.SET_LOADING,
    payload: false,
  });
}

function* addDirectMessage({ payload }: any) {
  const message: TDirectMessage = payload;
  try {
    const receiver = yield* select((store: TStore) => store.directChat.receiver);

    if (message.archivedId) {
      yield new DirectMessageModel(message).save();
    }

    if (message.from === receiver || message.from === 'me') {
      const messages: TDirectMessage[] = yield select((store: TStore) => store.directChat.messages);

      //console.log('Payload: ', payload, message);
      DB.addDirectMessage(message);

      yield put({
        type: directChatActionTypes.GET_MESSAGES_SUCCESS,
        payload: removeDuplicates([...messages, message]),
      });
    } else {
      // wtf? TODO:
      if (!isDuplicate(payload)) {
        // increment unreads notifications
        incrementUnreadForContact(payload.from);
        yield put({ type: directChatActionTypes.GET_UNREADS_REQUEST });

        // update lastMessages or append new one
        const lastMessages: TLastMessage[] = yield select(
          (store: TStore) => store.directChat.lastMessages,
        );
        let newLastMessages = [...lastMessages];

        if (lastMessages.find(({ from }) => from === payload.from)) {
          newLastMessages = lastMessages.map(({ from, body, stamp, attachmentName }) => ({
            from,
            stamp: from === payload.from ? payload.stamp : stamp,
            body: from === payload.from ? payload.body : body,
            attachmentName: from === payload.from ? payload.attachment?.name || '' : attachmentName,
          }));
          newLastMessages = sortLastMessages(newLastMessages);
        } else {
          newLastMessages.unshift({
            from: payload.from,
            body: payload.body,
            stamp: payload.stamp,
            attachmentName: payload.attachment?.name || '',
          });
        }

        // console.log('payload', payload);
        // const contacts: TChatContact[] = yield select((store: TStore) => store.directChat.contacts);
        // console.log(contacts);

        // showNotification({
        //   title: payload.alertTitle,
        //   copy: payload.body,
        // });

        yield put({
          type: directChatActionTypes.GET_LAST_MESSAGES_SUCCESS,
          payload: newLastMessages,
        });
      }
    }
  } catch (err) {
    console.log(err);
  }
}

function* getLastMessages() {
  yield put({
    type: directChatActionTypes.SET_LOADING_LAST_MESSAGES,
    payload: true,
  });
  try {
    const connection = yield* call(connectionService.waitForConnection);
    const jabberId = yield* select((store: TStore) => store.users.currentUser?.jabberId);

    const result: any = yield* call(getMessagesFromLastMonth, connection, jabberId as string);
    const filtered: any[] = result.filter((el: any) => el.to === jabberId);

    // order and group messages
    const orderedMsgs: any[] = filtered.sort((a: any, b: any) =>
      moment(b.stamp).diff(moment(a.stamp)),
    );

    const jidsAlreadyIn: string[] = [];
    const groupedMessages: any[] = [];
    for (let i = 0; i < orderedMsgs.length; i += 1) {
      if (!jidsAlreadyIn.includes(orderedMsgs[i].from)) {
        jidsAlreadyIn.push(orderedMsgs[i].from);
        groupedMessages.push(orderedMsgs[i]);
      }
    }

    //const sortedLastMessages = sortLastMessages(lastMessages);
    yield put({
      type: directChatActionTypes.GET_LAST_MESSAGES_SUCCESS,
      payload: groupedMessages,
    });
  } catch (e) {
    console.error('Saga error getLastMessages: ', e);
  } finally {
    yield put({
      type: directChatActionTypes.SET_LOADING_LAST_MESSAGES,
      payload: false,
    });
  }
}

function* getUnreadsRequest() {
  try {
    const unreads = getUnreads();

    yield put({
      type: directChatActionTypes.GET_UNREADS_SUCCESS,
      payload: unreads,
    });
  } catch {}
}

function* directChatSaga() {
  yield takeLatest(directChatActionTypes.GET_MESSAGES_REQUEST, getMessagesRequest);
  yield takeLatest(directChatActionTypes.ADD_DIRECT_MESSAGE, addDirectMessage);
  yield takeLatest(directChatActionTypes.GET_LAST_MESSAGES_REQUEST, getLastMessages);
  yield takeLatest(directChatActionTypes.GET_UNREADS_REQUEST, getUnreadsRequest);
}

export default directChatSaga;
